import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { PostImageGallery } from "@/components/PostImageGallery";
import { LinkifiedText } from "@/components/LinkifiedText";
import SpotifyEmbed from "@/components/SpotifyEmbed";
import { supabase } from "@/integrations/supabase/client";
import { buildFeedTitle, getSpotifyItemLabel, mergeFeedDescriptions, parseBlogImages, type ArticleRow, type FeedItemRow, type PlaylistRow } from "@/lib/contentModels";
import { readLocalQueryCache } from "@/lib/localQueryCache";
import { clavePostDetalle } from "@/lib/primeraFotoDelBuild";
import { setDocumentSeo, SITE_ORIGIN, truncateSeoDescription, truncateSeoTitle } from "@/lib/seoDocument";
import { getSpotifyCanonicalUrl, SPOTIFY_PLAYLIST_EMBED_HEIGHT } from "@/lib/spotify";

const MONTHS_ES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

const toSafeExternalUrl = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const raw = value.trim();
  if (!raw) return null;
  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== "https:") return null;
    return parsed.toString();
  } catch {
    return null;
  }
};

const getEntryDateMeta = (entry: ArticleRow): { dateLabel: string; timeLabel: string | null } => {
  if (entry.fecha_post) {
    const parsedDate = new Date(`${entry.fecha_post}T00:00:00`);
    if (!Number.isNaN(parsedDate.getTime())) {
      const timeFromFecha = entry.fecha?.match(/^\d{1,2}:\d{2}$/)?.[0] ?? null;
      return {
        dateLabel: parsedDate.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }),
        timeLabel: timeFromFecha,
      };
    }
  }
  const raw = entry.link ?? entry.source_archive_url ?? "";
  const m = raw.match(/\/(\d{4})\/(\d{1,2})(?:\/(\d{1,2}))?\//);
  if (m) {
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = m[3] ? Number(m[3]) : null;
    if (year && month >= 1 && month <= 12) {
      const dateLabel =
        day
          ? `${day} ${MONTHS_ES[month - 1]} ${year}`
          : entry.created_at && !Number.isNaN(new Date(entry.created_at).getTime())
            ? new Date(entry.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })
            : `${MONTHS_ES[month - 1]} ${year}`;
      const timeFromFecha = entry.fecha?.match(/^\d{1,2}:\d{2}$/)?.[0] ?? null;
      return { dateLabel, timeLabel: timeFromFecha };
    }
  }

  const timeFromFecha = entry.fecha?.match(/^\d{1,2}:\d{2}$/)?.[0] ?? null;
  if (entry.created_at) {
    const d = new Date(entry.created_at);
    if (!Number.isNaN(d.getTime())) {
      return {
        dateLabel: d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }),
        timeLabel: timeFromFecha,
      };
    }
  }

  return { dateLabel: entry.fecha || "Sin fecha", timeLabel: null };
};

const PostShareBar = ({ path, title }: { path: string; title: string }) => {
  const publicUrl = typeof window !== "undefined" ? `${window.location.origin}${path}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Enlace copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const shareNative = async () => {
    if (!navigator.share) {
      await copyLink();
      return;
    }
    try {
      await navigator.share({ title, url: publicUrl });
    } catch (e) {
      if ((e as Error).name !== "AbortError") await copyLink();
    }
  };

  return (
    <div className="mt-3 flex gap-2 border-t border-border pt-3">
      <button type="button" onClick={copyLink} className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground">
        Copiar enlace
      </button>
      <button type="button" onClick={shareNative} className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground">
        Compartir
      </button>
    </div>
  );
};

/**
 * Lo que devuelve la consulta del detalle. Nombrado porque es tambien la forma
 * exacta que el build hornea en el HTML de cada pagina.
 */
type DetalleDelPost =
  | { kind: "playlist"; data: PlaylistRow | null }
  | { kind: "blog-legacy"; data: ArticleRow | null }
  | { kind: "articulos"; data: ArticleRow | null }
  | { kind: "feed"; data: FeedItemRow[] }
  | null;

const PostDetail = () => {
  const navigate = useNavigate();
  const { kind, id } = useParams<{ kind: string; id: string }>();
  const backLinkCls =
    "mb-5 inline-flex cursor-pointer items-center border-none bg-transparent p-0 text-left font-inherit text-sm font-medium text-muted-foreground transition-colors hover:text-primary";

  const { data: post, isPending, isError, error } = useQuery({
    queryKey: ["post", kind, id],
    queryFn: async (): Promise<DetalleDelPost> => {
      if (!kind || !id) return null;
      if (kind === "playlist") {
        const { data, error: e } = await (supabase.from as any)("playlists").select("*").eq("id", id).maybeSingle();
        if (e) throw e;
        return { kind, data: data as PlaylistRow | null };
      }
      if (kind === "blog-legacy") {
        const asNum = Number(id);
        const { data, error: e } = await (supabase.from as any)("blogs").select("*").eq("post_num", asNum).maybeSingle();
        if (e) throw e;
        return { kind: "blog-legacy", data: data as ArticleRow | null };
      }
      if (kind === "blog" || kind === "articulos") {
        const asNum = Number(id);
        const { data, error: e } = await supabase.from("articulos" as never).select("*").eq("post_num", asNum).maybeSingle();
        if (e) throw e;
        return { kind: "articulos", data: data as ArticleRow | null };
      }
      if (kind === "feed") {
        const { data, error: e } = await (supabase.from as any)("feed_items")
          .select("*")
          .eq("post_id", decodeURIComponent(id))
          .order("item_index", { ascending: true });
        if (e) throw e;
        return { kind, data: (data ?? []) as FeedItemRow[] };
      }
      return null;
    },
    enabled: Boolean(id && kind),
    /*
     * El build deja cada articulo, blog, concierto y playlist escrito en su
     * propio HTML (ver src/lib/primeraFotoDelBuild.ts). Sin esto, la pagina no
     * tenia nada que pintar hasta que volvia la consulta: medido en
     * produccion, 905 ms hasta la respuesta y 1843 hasta ver la foto.
     * `initialDataUpdatedAt: 0` lo marca caducado, asi que la consulta sale
     * igual y manda ella.
     */
    initialData: () =>
      kind && id ? readLocalQueryCache<DetalleDelPost>(clavePostDetalle(kind, id)) : undefined,
    initialDataUpdatedAt: 0,
  });

  useEffect(() => {
    if (!id || !kind || isPending || isError) return;
    if (!post || (Array.isArray(post.data) && post.data.length === 0)) return;

    const canonical =
      typeof window !== "undefined"
        ? `${SITE_ORIGIN}${window.location.pathname}${window.location.search}`
        : `${SITE_ORIGIN}/post/${kind}/${id}`;

    if (post.kind === "feed") {
      const rows = post.data as FeedItemRow[];
      const mergedDesc = rows.reduce((acc, r) => mergeFeedDescriptions(acc, r.descripcion), "").trim();
      const titleText = mergedDesc.length > 0 ? mergedDesc : buildFeedTitle(rows[0]);
      setDocumentSeo({
        title: truncateSeoTitle(`${titleText} | Popistant — conciertos indie`),
        description: truncateSeoDescription(
          mergedDesc || `${titleText}. Conciertos y música en vivo en Popistant (popistant.es).`,
        ),
        canonical,
      });
      return;
    }

    if (post.kind === "playlist") {
      const playlist = post.data as PlaylistRow;
      setDocumentSeo({
        title: truncateSeoTitle(`${playlist.title} — playlist | Popistant`),
        description: truncateSeoDescription(
          playlist.description?.trim() || `${playlist.title}. Playlist en Spotify desde Popistant.`,
        ),
        canonical,
      });
      return;
    }

    const entry = post.data as ArticleRow;
    const titulo = entry.titulo?.trim() || "Artículo";
    const excerpt = (entry.descripcion || "").replace(/\s+/g, " ").trim();
    const label = post.kind === "blog-legacy" ? "blog retro | Popistant" : "artículo música indie | Popistant";
    setDocumentSeo({
      title: truncateSeoTitle(`${titulo} — ${label}`),
      description: truncateSeoDescription(
        excerpt || `${titulo}. ${post.kind === "blog-legacy" ? "Archivo del blog retro" : "Artículo"} en popistant.es.`,
      ),
      canonical,
    });
  }, [post, isPending, isError, id, kind]);

  if (!id || !kind) {
    return <div className="py-20 text-center"><h1 className="font-display text-2xl font-bold">Enlace no valido</h1><Link to="/" className="mt-4 inline-flex items-center text-sm font-medium text-muted-foreground transition-colors hover:text-primary">← Volver</Link></div>;
  }
  if (isPending) {
    return <div className="mx-auto max-w-3xl px-4 py-12 animate-pulse"><div className="h-6 w-32 rounded bg-muted" /><div className="mt-4 h-10 w-2/3 rounded bg-muted" /><div className="mt-6 aspect-video rounded-xl bg-muted" /></div>;
  }
  if (isError) {
    return <div className="py-20 text-center"><h1 className="font-display text-2xl font-bold">Error</h1><p className="mt-2 text-sm text-muted-foreground">{error instanceof Error ? error.message : "Error"}</p><Link to="/" className="mt-4 inline-flex items-center text-sm font-medium text-muted-foreground transition-colors hover:text-primary">← Volver</Link></div>;
  }
  if (!post || !post.data || (Array.isArray(post.data) && post.data.length === 0)) {
    return <div className="py-20 text-center"><h1 className="font-display text-2xl font-bold">No encontrado</h1><Link to="/" className="mt-4 inline-flex items-center text-sm font-medium text-muted-foreground transition-colors hover:text-primary">← Volver</Link></div>;
  }

  if (post.kind === "feed") {
    const rows = post.data as FeedItemRow[];
    const mergedDesc = rows.reduce((acc, r) => mergeFeedDescriptions(acc, r.descripcion), "").trim();
      const title = mergedDesc.length > 0 ? mergedDesc : buildFeedTitle(rows[0]);
      const galleryUrls = rows.map((r) => r.archivo).filter(Boolean) as string[];
      const originalLink = toSafeExternalUrl(rows.find((r) => r.link)?.link ?? null);
      const created = rows[0]?.fecha_utc ? new Date(rows[0].fecha_utc).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }) : "Sin fecha";
      const path = `/post/feed/${encodeURIComponent(rows[0].post_id)}`;
      return (
        <article className="mx-auto max-w-5xl px-4 py-8">
          <button type="button" onClick={() => navigate(-1)} className={backLinkCls}>
            ← Volver
          </button>
        <div className="mb-2 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="font-medium uppercase tracking-widest">{created}</span>
        </div>
        <div className="grid gap-6 lg:grid-cols-[1.3fr_0.7fr]">
          {/* Es lo que se viene a ver, y esta arriba del todo: no espera al
              observador, que ademas aqui no tiene nada que decidir. */}
          <PostImageGallery urls={galleryUrls} alt={title} layout="carousel" prioritaria />
          <div>
            <h1 className="mt-2 whitespace-pre-line font-display text-2xl font-extrabold uppercase tracking-tight md:text-3xl">{title}</h1>
            {originalLink ? (
              <a
                href={originalLink}
                target="_blank"
                rel="noreferrer"
                className="mt-5 inline-block text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline"
              >
                Abrir en Instagram {"->"}
              </a>
            ) : (
              <span className="mt-5 inline-block text-sm text-muted-foreground/70" title="Este post aun no tiene URL original">
                Abrir en Instagram {"->"}
              </span>
            )}
            <PostShareBar path={path} title={title} />
          </div>
        </div>
      </article>
    );
  }

  if (post.kind === "playlist") {
    const playlist = post.data as PlaylistRow;
    const date = new Date(playlist.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
    const path = `/post/playlist/${playlist.id}`;
    const itemLabel = getSpotifyItemLabel(playlist.spotify_url);
    return (
      <article className="mx-auto max-w-3xl px-4 py-8">
        <button type="button" onClick={() => navigate(-1)} className={backLinkCls}>
          ← Volver
        </button>
        <div className="mb-2 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="font-medium uppercase tracking-widest">{itemLabel}</span>
          <span>{date}</span>
        </div>
        <h1 className="mt-2 font-display text-xl font-bold uppercase tracking-tight md:text-2xl">{playlist.title}</h1>
        {playlist.description && <LinkifiedText text={playlist.description} className="mt-4 text-sm leading-7 text-muted-foreground" />}
        {playlist.spotify_url && (
          <div className="mt-6">
            <SpotifyEmbed url={playlist.spotify_url} height={SPOTIFY_PLAYLIST_EMBED_HEIGHT} />
          </div>
        )}
        {getSpotifyCanonicalUrl(playlist.spotify_url ?? "") && (
          <a
            href={getSpotifyCanonicalUrl(playlist.spotify_url ?? "") ?? undefined}
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-block text-xs font-medium text-primary hover:underline"
          >
            Abrir en Spotify {"->"}
          </a>
        )}
        <PostShareBar path={path} title={playlist.title} />
      </article>
    );
  }

  const entry = post.data as ArticleRow;
  const images = parseBlogImages(entry.imagenes);
  const legacyLabels =
    post.kind === "blog-legacy"
      ? (entry.labels ?? "")
          .split(";")
          .map((item) => item.trim())
          .filter(Boolean)
      : [];
  const blogId = entry.post_num;
  const isLegacyBlogArchive = post.kind === "blog-legacy";
  const path = isLegacyBlogArchive ? `/post/blog-legacy/${entry.post_num}` : `/post/articulos/${blogId}`;
  const { dateLabel } = getEntryDateMeta(entry);
  const hasImages = images.length > 0;

  const showLegacyMeta = isLegacyBlogArchive && (legacyLabels.length > 0 || toSafeExternalUrl(entry.link));
  const legacyOriginalUrl = isLegacyBlogArchive ? toSafeExternalUrl(entry.link) : null;

  return (
    <article className="mx-auto max-w-[80rem] px-5 py-8 pb-16 md:px-9 md:pb-24">
      <div className="mx-auto max-w-5xl">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={backLinkCls}
        >
          ← Volver
        </button>
        <div className="space-y-7 lg:space-y-10">
          {hasImages ? (
            <div className="grid gap-7 lg:grid-cols-[minmax(0,1.15fr)_minmax(280px,440px)] lg:items-start lg:gap-12">
              <div className="min-w-0 lg:justify-self-stretch">
                <div className="mx-auto w-fit max-w-full overflow-hidden rounded-xl">
                  <PostImageGallery
                    urls={images}
                    alt={entry.titulo}
                    layout="carousel"
                    maxHeight={680}
                    imgClassName="object-contain"
                  />
                </div>
              </div>
              <div className="flex min-w-0 flex-col justify-start gap-3 lg:py-2">
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <span className="font-medium uppercase tracking-[0.2em]">{dateLabel}</span>
                </div>
                <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight md:text-3xl">{entry.titulo}</h1>
                {showLegacyMeta && (
                  <div className="space-y-2">
                    {legacyLabels.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {legacyLabels.map((label) => (
                          <span
                            key={`${entry.post_num}-${label}`}
                            className="rounded-md border border-border bg-muted/50 px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground"
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <PostShareBar path={path} title={entry.titulo} />
                {legacyOriginalUrl && (
                  <a
                    href={legacyOriginalUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-block text-sm font-medium text-primary underline-offset-4 hover:underline"
                  >
                    Ver publicacion original {"->"}
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl space-y-4">
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="font-medium uppercase tracking-[0.2em]">{dateLabel}</span>
              </div>
              <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight md:text-3xl">{entry.titulo}</h1>
              {showLegacyMeta && (
                <div className="space-y-2">
                  {legacyLabels.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {legacyLabels.map((label) => (
                        <span
                          key={`${entry.post_num}-${label}`}
                          className="rounded-md border border-border bg-muted/50 px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground"
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <PostShareBar path={path} title={entry.titulo} />
              {legacyOriginalUrl && (
                <a
                  href={legacyOriginalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-block text-sm font-medium text-primary underline-offset-4 hover:underline"
                >
                  Ver publicacion original {"->"}
                </a>
              )}
            </div>
          )}
          <div className="mx-auto w-full max-w-[68rem]">
            {entry.descripcion ? (
              <LinkifiedText text={entry.descripcion} className="text-base leading-7 text-foreground/90" />
            ) : (
              <p className="text-muted-foreground">Sin texto.</p>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default PostDetail;
